import React, { useState, useEffect } from "react";
import API from "api/endpoints";
import { Button, Card, Empty, message, Space, Typography, Select, Tag, Input } from "antd";
import axiosInstance from "api/axiosInstance";
import { LeaveRequestEnum, LeaveRequestEnumLabel } from "Constants/Enums/LeaveRequestEnum";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

export default function LeaveRequest() {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [approvingRequest, setApprovingRequest] = useState(null);
  const [approverRemarks, setApproverRemarks] = useState("");
  const [actionLoading, setActionLoading] = useState(false); // Separate loading for approve/reject actions

  useEffect(() => {
    fetchLeaveRequests();
    fetchDepartments();
  }, []);

  // Fetch all departments
  async function fetchDepartments() {
    setDepartmentsLoading(true);
    try {
      const response = await axiosInstance.get(API.department.getList);
      setDepartments(response.data);
    } catch (error) {
      console.error("Error fetching departments:", error);
      message.error("Failed to fetch departments");
    } finally {
      setDepartmentsLoading(false);
    }
  }

  // Fetch leave requests with optional department filter
  async function fetchLeaveRequests(departmentId = null) {
    setLoading(true);
    try {
      let url = API.leaveRequest.getPendingRequests;

      if (typeof url === "function") {
        url = url(departmentId);
      }

      const response = await axiosInstance.get(url);
      setLeaveRequests(response.data);
    } catch (error) {
      console.error(" Error fetching leave requests:", error);
      message.error("Failed to fetch leave requests");
    } finally {
      setLoading(false);
    }
  }

  // Filter leave requests based on selected department (client-side filtering)
  const getFilteredLeaveRequests = () => {
    if (!selectedDepartment) {
      return leaveRequests.filter((request) => request.status !== LeaveRequestEnum.Approved);
    }

    return leaveRequests.filter((request) => {
      const userDepartment = request.departmentId || request.employee?.departmentId;
      return (
        request.status !== LeaveRequestEnum.Approved &&
        (userDepartment === selectedDepartment ||
          request.employee?.departmentId === selectedDepartment)
      );
    });
  };

  // Handle department change
  const handleDepartmentChange = (value) => {
    setSelectedDepartment(value);
  };

  // NEW: Handle approve/reject using the correct API structure
  const handleApproveReject = async (requestId, isApproved) => {
    try {
      setActionLoading(true);

      // Now it's a simple string URL, no function call needed
      const url = API.leaveRequest.createHRReview;

      const payload = {
        leaveRequestId: requestId, // ID goes in the body
        isApproved: isApproved,
        approverRemarks: approverRemarks || `Leave request ${isApproved ? "approved" : "rejected"}`,
      };

      const response = await axiosInstance.put(url, payload);

      if (response.data) {
        message.success(`Leave request ${isApproved ? "approved" : "rejected"} successfully`);
        setApproverRemarks("");
        setApprovingRequest(null);
        await fetchLeaveRequests(selectedDepartment);
      }
    } catch (error) {
      console.error(" Error with PUT:", error);
      console.error("Error details:", error.response?.data);
      message.error(`Failed to ${isApproved ? "approve" : "reject"} leave request`);
    } finally {
      setActionLoading(false);
    }
  };

  // Show approval modal - FIXED: No loading here
  const showApprovalModal = (requestId, isApproved) => {
    setApprovingRequest(requestId);
    setApproverRemarks("");
  };

  // Cancel approval action - FIXED: No loading here
  const cancelApproval = () => {
    setApprovingRequest(null);
    setApproverRemarks("");
  };

  // Using your enums for status display
  const getStatusTag = (status) => {
    const colorMap = {
      [LeaveRequestEnum.Pending]: "orange",
      [LeaveRequestEnum.Approved]: "green",
      [LeaveRequestEnum.Rejected]: "red",
      [LeaveRequestEnum.Cancelled]: "gray",
    };

    return (
      <Tag color={colorMap[status] || "default"}>{LeaveRequestEnumLabel[status] || "Unknown"}</Tag>
    );
  };

  const rowStyle = {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    alignItems: "center",
  };

  const filteredRequests = getFilteredLeaveRequests();

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <span style={{ fontSize: "16px", fontWeight: "bold" }}>Leave Requests</span>
          <Select
            placeholder="All Departments"
            style={{ width: 250 }}
            onChange={handleDepartmentChange}
            value={selectedDepartment}
            allowClear
            onClear={() => {
              setSelectedDepartment(null);
              fetchLeaveRequests();
            }}
            loading={departmentsLoading}
            disabled={departmentsLoading}
          >
            <Option value={null}>All Departments</Option>
            {departments.map((dept) => (
              <Option key={dept.id || dept.departmentId} value={dept.id || dept.departmentId}>
                {dept.name || dept.departmentName}
              </Option>
            ))}
          </Select>
        </div>
      }
      className="card-professional"
      loading={loading}
      extra={
        selectedDepartment && (
          <Tag color="blue" style={{ marginRight: "10px" }}>
            Showing:{" "}
            {departments.find((d) => (d.id || d.departmentId) === selectedDepartment)?.name ||
              selectedDepartment}
          </Tag>
        )
      }
    >
      {filteredRequests.length > 0 ? (
        <div className="row">
          {filteredRequests.map((request) => (
            <div key={request.id} className="col-md-6">
              <Card
                style={{ marginBottom: 15 }}
                size="small"
                styles={{ body: { padding: "16px" } }}
              >
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  {/* Department Badge */}
                  {(request.department || request.employee?.departmentName) && (
                    <div style={{ textAlign: "right" }}>
                      <Tag color="blue">Dept: {request.employee?.departmentName}</Tag>
                    </div>
                  )}

                  {/* Employee Information */}
                  <Space style={rowStyle}>
                    <Text strong>Employee:</Text>
                    <Text>
                      {request.employee?.firstName} {request.employee?.lastName}
                    </Text>
                  </Space>

                  {/* Leave Type */}
                  <Space style={rowStyle}>
                    <Text strong>Leave Type:</Text>
                    <Tag color="purple">{request.leavePolicy?.policyName}</Tag>
                  </Space>

                  {/* Duration */}
                  <Space style={rowStyle}>
                    <Text strong>Duration:</Text>
                    <Text>{request.amountDays || request.totalDays} Days</Text>
                  </Space>

                  {/* Date Range */}
                  <Space style={rowStyle}>
                    <Text strong>Date Range:</Text>
                    <Text>
                      {request.fromDate} to {request.toDate}
                    </Text>
                  </Space>

                  {/* Granted Days */}
                  {request.grantedDays && (
                    <Space style={rowStyle}>
                      <Text strong>Granted Days/Year:</Text>
                      <Text>{request.leavePolicy?.grantedDaysPerYear} Days</Text>
                    </Space>
                  )}

                  {/* Status - USING YOUR ENUMS */}
                  <Space style={rowStyle}>
                    <Text strong>Status:</Text>
                    {getStatusTag(request.status)}
                  </Space>

                  {/* Reason */}
                  {request.reason && (
                    <Space style={rowStyle}>
                      <Text strong>Reason:</Text>
                      <Text style={{ maxWidth: "200px", textAlign: "right" }}>
                        {request.reason}
                      </Text>
                    </Space>
                  )}
                </Space>

                {/* Action Buttons - Only show for pending requests */}
                {request.status === LeaveRequestEnum.Pending && (
                  <div
                    style={{
                      marginTop: 16,
                      textAlign: "right",
                      borderTop: "1px solid #f0f0f0",
                      paddingTop: "12px",
                    }}
                  >
                    {approvingRequest === request.id ? (
                      <Space direction="vertical" style={{ width: "100%" }} size="small">
                        <TextArea
                          placeholder="Add remarks (optional)"
                          value={approverRemarks}
                          onChange={(e) => setApproverRemarks(e.target.value)}
                          rows={2}
                          disabled={actionLoading} // Disable textarea during loading
                        />
                        <Space>
                          <Button
                            className="btn-primary"
                            size="small"
                            loading={actionLoading} // Use the separate action loading state
                            onClick={() => handleApproveReject(request.id, true)}
                            disabled={actionLoading}
                          >
                            Confirm Approve
                          </Button>
                          <Button
                            danger
                            size="small"
                            loading={actionLoading} // Use the separate action loading state
                            onClick={() => handleApproveReject(request.id, false)}
                            disabled={actionLoading}
                          >
                            Confirm Reject
                          </Button>
                          <Button
                            size="small"
                            onClick={cancelApproval}
                            disabled={actionLoading} // Disable cancel during loading
                          >
                            Cancel
                          </Button>
                        </Space>
                      </Space>
                    ) : (
                      <Space>
                        <Button
                          className="btn-primary"
                          size="small"
                          onClick={() => showApprovalModal(request.id, true)}
                          disabled={actionLoading} // Disable buttons during any action loading
                        >
                          Approve
                        </Button>
                        <Button
                          danger
                          size="small"
                          onClick={() => showApprovalModal(request.id, false)}
                          disabled={actionLoading} // Disable buttons during any action loading
                        >
                          Reject
                        </Button>
                      </Space>
                    )}
                  </div>
                )}
              </Card>
            </div>
          ))}
        </div>
      ) : (
        <Empty
          description={
            selectedDepartment
              ? `No pending leave requests in ${
                  departments.find((d) => (d.id || d.departmentId) === selectedDepartment)?.name ||
                  selectedDepartment
                } department`
              : "No pending leave requests available"
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {/* Summary Stats */}
      {filteredRequests.length > 0 && (
        <div style={{ marginTop: 16, padding: "12px", background: "#f5f5f5", borderRadius: "6px" }}>
          <Text type="secondary">
            Showing {filteredRequests.length} request{filteredRequests.length !== 1 ? "s" : ""}
            {selectedDepartment &&
              ` in ${
                departments.find((d) => (d.id || d.departmentId) === selectedDepartment)?.name ||
                selectedDepartment
              } department`}
          </Text>
        </div>
      )}
    </Card>
  );
}
